"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { HiBell, HiChevronDoubleLeft, HiChevronDoubleRight, HiOutlineArchiveBox, HiOutlineArchiveBoxArrowDown, HiOutlineCheck, HiOutlineCog6Tooth, HiOutlineFunnel, HiXMark } from "react-icons/hi2";
import { BrowserNotificationsBanner } from "@/components/notifications/browser-notifications-banner";
import { EprescribingBanner } from "@/components/notifications/eprescribing-banner";
import { NotificationItem } from "@/components/notifications/notification-item";
import { SoundBlockedBanner } from "@/components/notifications/sound-blocked-banner";
import { useProgressNavigation } from "@/components/progress-bar";
import { Button } from "@/components/ui";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator, DropdownMenuTrigger, DropdownSettingsButton } from "@/components/ui/dropdown";
import { queryClient } from "@/config/react-query";
import { useTenant } from "@/contexts/tenant-provider";
import { useUser } from "@/contexts/user-provider";
import { useClinicClock } from "@/hooks/use-clinic-clock";
import { alertTypeLabel } from "@/lib/alert-type-label";
import { type ClinicClock } from "@/lib/clinic-clock";
import { ReferenceType, getListRouteForReferences, getRouteForReference, listNounForReference, listableReferenceType, parseNotificationContent, referenceIdsOfType } from "@/lib/notification-reference";
import { isTypeStack } from "@/lib/notification-stack";
import { cx } from "@/lib/tailwind-utils";
import { useToast } from "@/lib/use-toast";
import { AlertType } from "@/types/enums/alert-type";
import { NotificationAutoMarkRead } from "@/types/enums/notification-auto-mark-read";
import { NotificationMethod } from "@/types/enums/notification-method";
import { UserType } from "@/types/enums/user-type";
import { ProviderNotificationSummary } from "@/types/notification";
import { patientSidebarDefinition } from "@/features/sidebar/patient-sidebar";
import { APPLY_NOTIFICATION_PRESET_MUTATION_KEY, NOTIFICATIONS_PER_PAGE, getCombinedNotifications, useArchiveNotificationsMutation, useBulkNotificationActionMutation, useMarkNotificationsReadMutation, useMarkNotificationsUnreadMutation, useMuteAlertTypeMutation, useNotificationPreferences, useNotificationsInfinite } from "@/query/notifications/notifications";
import { useNotificationsDrawerStore } from "@/stores/notifications-drawer-store";
import { useRightSidebarStore } from "@/stores/right-sidebar-store";
import { useTeamChatDrawerStore } from "@/stores/team-chat-store";
type FilterType = "all" | "unread" | "read";
const QUICK_ACTION_LABEL: Partial<Record<ReferenceType, string>> = {
  patient: "View Patient",
  call: "View Call",
  chat: "View Chat",
  message: "View Chat",
  event: "Open in Calendar",
  waitlist: "View Waitlist Request",
  form: "View Form",
  payment: "View Payment",
  charge: "View Charge",
  invoice: "View Invoice",
  adjustment: "View Charge",
  note: "View Note",
  shift: "View Shifts",
  stock: "View Stock",
  item: "View Products",
  shipment: "View Purchase Orders",
  po: "View Purchase Orders",
  teamconversation: "View Whisper",
  socialconversation: "View Social Message",
  opportunity: "View Lead",
  lab: "View Lab Results",
  cashbalance: "View Cash Balances",
  coupa_drift: "View Coupa Drift",
  coupa_reconciliation: "Open Reconciliation",
  approval: "View Request"
};
const CHECKLIST_ALERT_TYPES = new Set<AlertType>([AlertType.APPOINTMENT_CHECKLIST_FILLED_FORM, AlertType.APPOINTMENT_CHECKLIST_CHECKED_IN, AlertType.APPOINTMENT_CHECKLIST_WITH_MA, AlertType.APPOINTMENT_CHECKLIST_MEASUREMENT_TAKEN, AlertType.APPOINTMENT_CHECKLIST_DOSE_TAKEN, AlertType.APPOINTMENT_CHECKLIST_WITH_PROVIDER, AlertType.APPOINTMENT_CHECKLIST_NOTE_TAKEN, AlertType.APPOINTMENT_CHECKLIST_PAYMENT_MADE, AlertType.APPOINTMENT_CHECKLIST_REQUEST_GFE, AlertType.APPOINTMENT_CHECKLIST_PICTURE_TAKEN, AlertType.APPOINTMENT_CHECKLIST_CARE_PLAN, AlertType.APPOINTMENT_CHECKLIST_NOTE_SIGNED_OFF, AlertType.APPOINTMENT_CHECKLIST_REBOOK, AlertType.APPOINTMENT_CHECKLIST_PROFILE_PHOTO]);
function parsePatientDisplayName(display: string) {
  const trimmed = display.trim();
  if (trimmed.includes(",")) {
    const [last, ...rest] = trimmed.split(",").map(s => s.trim());
    return {
      firstName: rest.join(" ") || "",
      lastName: last
    };
  }
  const nameParts = trimmed.split(" ").filter(Boolean);
  return {
    firstName: nameParts[0] || "",
    lastName: nameParts.slice(1).join(" ")
  };
}
function groupNotifications(notifications: ProviderNotificationSummary[], clock: ClinicClock) {
  const now = new Date();
  // Today starts at the clinic's midnight, the clock the times are shown on.
  const startOfToday = clock.dayRange(clock.today()).start;
  const oneWeekAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
  const today: ProviderNotificationSummary[] = [];
  const thisWeek: ProviderNotificationSummary[] = [];
  const older: ProviderNotificationSummary[] = [];
  notifications.forEach(notification => {
    const createdDate = new Date(notification.createdDate);
    if (createdDate >= startOfToday) {
      today.push(notification);
    } else if (createdDate >= oneWeekAgo) {
      thisWeek.push(notification);
    } else {
      older.push(notification);
    }
  });
  return {
    today,
    thisWeek,
    older
  };
}
interface NotificationGroupProps {
  label: string;
  notifications: ProviderNotificationSummary[];
  getQuickActions: (n: ProviderNotificationSummary) => Array<{
    label: string;
    onClick: () => void;
  }>;
  onMarkAsRead: (ids: string[]) => void;
  onMarkAsUnread: (ids: string[]) => void;
  onArchive: (ids: string[]) => void;
  onMuteAlertType: (alertType: string) => void;
}
function NotificationGroup(t0) {
  const $ = _c(18);
  const {
    label,
    notifications,
    getQuickActions,
    onMarkAsRead,
    onMarkAsUnread,
    onArchive,
    onMuteAlertType
  } = t0;
  if (notifications.length === 0) {
    return null;
  }
  let t1;
  if ($[0] !== label) {
    t1 = <div className="px-4 py-2"><span className="text-caption font-semibold uppercase tracking-wider text-muted-foreground">{label}</span></div>;
    $[0] = label;
    $[1] = t1;
  } else {
    t1 = $[1];
  }
  let t2;
  if ($[2] !== getQuickActions || $[3] !== notifications || $[4] !== onArchive || $[5] !== onMarkAsRead || $[6] !== onMarkAsUnread || $[7] !== onMuteAlertType) {
    let t3;
    if ($[9] !== getQuickActions || $[10] !== onArchive || $[11] !== onMarkAsRead || $[12] !== onMarkAsUnread || $[13] !== onMuteAlertType) {
      t3 = notification => <NotificationItem key={notification.id} notification={notification} quickActions={getQuickActions(notification)} onMarkAsRead={onMarkAsRead} onMarkAsUnread={onMarkAsUnread} onArchive={onArchive} onMuteAlertType={onMuteAlertType} />;
      $[9] = getQuickActions;
      $[10] = onArchive;
      $[11] = onMarkAsRead;
      $[12] = onMarkAsUnread;
      $[13] = onMuteAlertType;
      $[14] = t3;
    } else {
      t3 = $[14];
    }
    t2 = notifications.map(t3);
    $[2] = getQuickActions;
    $[3] = notifications;
    $[4] = onArchive;
    $[5] = onMarkAsRead;
    $[6] = onMarkAsUnread;
    $[7] = onMuteAlertType;
    $[8] = t2;
  } else {
    t2 = $[8];
  }
  let t3;
  if ($[15] !== t1 || $[16] !== t2) {
    t3 = <>{t1}{t2}</>;
    $[15] = t1;
    $[16] = t2;
    $[17] = t3;
  } else {
    t3 = $[17];
  }
  return t3;
}
interface NotificationsPanelProps {
  className?: string;
  mode?: "desktop" | "mobile";
  onClose?: () => void;
}
export function NotificationsPanel(t0) {
  const $ = _c(126);
  const {
    className,
    mode: t1,
    onClose
  } = t0;
  const mode = t1 === undefined ? "desktop" : t1;
  const {
    currentUser
  } = useUser();
  const {
    tenant
  } = useTenant();
  let t2;
  if ($[0] !== tenant?.enabledModules) {
    t2 = tenant?.enabledModules?.includes("WHISPER") ?? false;
    $[0] = tenant?.enabledModules;
    $[1] = t2;
  } else {
    t2 = $[1];
  }
  const isWhisperEnabled = t2;
  const {
    toast
  } = useToast();
  const {
    navigateWithProgress
  } = useProgressNavigation();
  const {
    isOpen,
    isLocked,
    closeDrawer,
    lockDrawer
  } = useNotificationsDrawerStore();
  const [selectedFilterRead, setFilterRead] = useState(null);
  const isAutoMarkOnOpen = currentUser?.notificationAutoMarkRead === NotificationAutoMarkRead.ON_VIEW_DROPDOWN;
  const filterRead = isAutoMarkOnOpen && selectedFilterRead === "unread" ? "all" : selectedFilterRead ?? (isAutoMarkOnOpen ? "all" : "unread");
  const observerTarget = useRef(null);
  const t3 = filterRead === "all" ? null : filterRead === "read";
  let t4;
  if ($[2] !== t3) {
    t4 = {
      read: t3
    };
    $[2] = t3;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  const filters = t4;
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
    isLoading
  } = useNotificationsInfinite(filters, NOTIFICATIONS_PER_PAGE);
  let t5;
  if ($[4] !== data?.pages) {
    t5 = data?.pages.flatMap(_temp) ?? [];
    $[4] = data?.pages;
    $[5] = t5;
  } else {
    t5 = $[5];
  }
  const notifications = t5;
  const {
    data: t6
  } = useNotificationPreferences();
  let t7;
  if ($[6] !== t6) {
    t7 = t6 === undefined ? [] : t6;
    $[6] = t6;
    $[7] = t7;
  } else {
    t7 = $[7];
  }
  const preferences = t7;
  const {
    mutate: markAsReadMutation
  } = useMarkNotificationsReadMutation();
  const {
    mutate: markAsUnreadMutation
  } = useMarkNotificationsUnreadMutation();
  const {
    mutate: bulkActionMutation
  } = useBulkNotificationActionMutation();
  const {
    mutate: archiveMutation
  } = useArchiveNotificationsMutation();
  const {
    mutate: muteAlertTypeMutation
  } = useMuteAlertTypeMutation();
  const openPatientSidebar = useRightSidebarStore(_temp2);
  let t8;
  let t9;
  if ($[8] !== fetchNextPage || $[9] !== hasNextPage || $[10] !== isFetchingNextPage) {
    t8 = () => {
      if (!hasNextPage || isFetchingNextPage) {
        return;
      }
      const observer = new IntersectionObserver(entries => {
        if (entries[0].isIntersecting && hasNextPage && !isFetchingNextPage) {
          fetchNextPage();
        }
      }, {
        threshold: 0.1
      });
      const currentTarget = observerTarget.current;
      if (currentTarget) {
        observer.observe(currentTarget);
      }
      return () => {
        if (currentTarget) {
          observer.unobserve(currentTarget);
        }
      };
    };
    t9 = [hasNextPage, isFetchingNextPage, fetchNextPage];
    $[8] = fetchNextPage;
    $[9] = hasNextPage;
    $[10] = isFetchingNextPage;
    $[11] = t8;
    $[12] = t9;
  } else {
    t8 = $[11];
    t9 = $[12];
  }
  useEffect(t8, t9);
  let t10;
  if ($[13] !== currentUser?.notificationAutoMarkRead || $[14] !== isOpen || $[15] !== markAsReadMutation || $[16] !== notifications) {
    t10 = () => {
      if (isOpen && currentUser?.notificationAutoMarkRead === NotificationAutoMarkRead.ON_VIEW_DROPDOWN && notifications.length > 0) {
        const unreadNotifications = notifications.filter(_temp3);
        if (unreadNotifications.length > 0) {
          markAsReadMutation(unreadNotifications.map(_temp4));
        }
      }
    };
    $[13] = currentUser?.notificationAutoMarkRead;
    $[14] = isOpen;
    $[15] = markAsReadMutation;
    $[16] = notifications;
    $[17] = t10;
  } else {
    t10 = $[17];
  }
  const t11 = currentUser?.notificationAutoMarkRead;
  let t12;
  if ($[18] !== isOpen || $[19] !== markAsReadMutation || $[20] !== notifications || $[21] !== t11) {
    t12 = [isOpen, t11, notifications, markAsReadMutation];
    $[18] = isOpen;
    $[19] = markAsReadMutation;
    $[20] = notifications;
    $[21] = t11;
    $[22] = t12;
  } else {
    t12 = $[22];
  }
  useEffect(t10, t12);
  let t13;
  if ($[23] !== markAsReadMutation || $[24] !== toast) {
    t13 = notificationIds => {
      markAsReadMutation(notificationIds, {
        onError: () => {
          toast({
            title: "Error",
            description: "Failed to mark notifications as read. Please try again.",
            variant: "error"
          });
        }
      });
    };
    $[23] = markAsReadMutation;
    $[24] = toast;
    $[25] = t13;
  } else {
    t13 = $[25];
  }
  const handleMarkAsRead = t13;
  let t14;
  if ($[26] !== markAsUnreadMutation || $[27] !== toast) {
    t14 = notificationIds_0 => {
      markAsUnreadMutation(notificationIds_0, {
        onError: () => {
          toast({
            title: "Error",
            description: "Failed to mark notifications as unread. Please try again.",
            variant: "error"
          });
        }
      });
    };
    $[26] = markAsUnreadMutation;
    $[27] = toast;
    $[28] = t14;
  } else {
    t14 = $[28];
  }
  const handleMarkAsUnread = t14;
  let t15;
  if ($[29] !== archiveMutation || $[30] !== toast) {
    t15 = notificationIds_1 => {
      archiveMutation(notificationIds_1, {
        onError: () => {
          toast({
            title: "Error",
            description: "Failed to archive notification. Please try again.",
            variant: "error"
          });
        }
      });
    };
    $[29] = archiveMutation;
    $[30] = toast;
    $[31] = t15;
  } else {
    t15 = $[31];
  }
  const handleArchive = t15;
  let t16;
  if ($[32] !== muteAlertTypeMutation || $[33] !== preferences || $[34] !== toast) {
    t16 = alertType => {
      if (queryClient.isMutating({
        mutationKey: APPLY_NOTIFICATION_PRESET_MUTATION_KEY
      }) > 0) {
        toast({
          title: "Still saving your notification level",
          description: "Try again in a moment."
        });
        return;
      }
      const preference = preferences.find(p => p.alertType === alertType && p.communicationMethod === NotificationMethod.IN_APP);
      if (!preference) {
        toast({
          title: "Already muted",
          description: "In-app notifications for this type are already off."
        });
        return;
      }
      muteAlertTypeMutation(preference.id, {
        onSuccess: () => {
          toast({
            title: "Muted",
            description: `You'll see less "${alertTypeLabel(alertType)}" notifications.`
          });
        },
        onError: () => {
          toast({
            title: "Error",
            description: "Failed to update notification settings.",
            variant: "error"
          });
        }
      });
    };
    $[32] = muteAlertTypeMutation;
    $[33] = preferences;
    $[34] = toast;
    $[35] = t16;
  } else {
    t16 = $[35];
  }
  const handleMuteAlertType = t16;
  let t17;
  if ($[36] !== closeDrawer || $[37] !== currentUser?.notificationAutoMarkRead || $[38] !== handleMarkAsRead || $[39] !== navigateWithProgress) {
    t17 = (path, notificationId) => {
      navigateWithProgress(path);
      closeDrawer();
      if (notificationId && currentUser?.notificationAutoMarkRead === NotificationAutoMarkRead.ON_QUICK_ACTION) {
        handleMarkAsRead([notificationId]);
      }
    };
    $[36] = closeDrawer;
    $[37] = currentUser?.notificationAutoMarkRead;
    $[38] = handleMarkAsRead;
    $[39] = navigateWithProgress;
    $[40] = t17;
  } else {
    t17 = $[40];
  }
  currentUser?.notificationAutoMarkRead;
  const handleNavigate = t17;
  const openTeamChat = useTeamChatDrawerStore(_temp5);
  const setActiveConversation = useTeamChatDrawerStore(_temp6);
  let t18;
  if ($[41] !== closeDrawer || $[42] !== currentUser?.notificationAutoMarkRead || $[43] !== handleMarkAsRead || $[44] !== handleNavigate || $[45] !== isWhisperEnabled || $[46] !== openPatientSidebar || $[47] !== openTeamChat || $[48] !== setActiveConversation || $[49] !== toast) {
    t18 = notification => {
      const typeStack = isTypeStack(notification);
      const listType = typeStack ? listableReferenceType(notification.alert.content) : null;
      const listNoun = listType && listNounForReference(listType);
      if (listType && listNoun) {
        return [{
          label: `View all ${notification.messageCount} ${listNoun}`,
          onClick: async () => {
            try {
              const earlier = await getCombinedNotifications(notification.id);
              const ids = referenceIdsOfType([notification, ...earlier].map(_temp7), listType);
              const list = getListRouteForReferences(listType, ids);
              if (list) {
                handleNavigate(list.route, notification.id);
              }
            } catch {
              toast({
                title: "Error",
                description: `Couldn't load the ${listNoun}. Please try again.`,
                variant: "error"
              });
            }
          }
        }];
      }
      if (typeStack) {
        return [];
      }
      const refs = parseNotificationContent(notification.alert.content);
      const seen = new Set();
      const actions = [];
      if (CHECKLIST_ALERT_TYPES.has(notification.alert.type)) {
        const patientRef = refs.find(_temp8);
        const eventRef = refs.find(_temp9);
        if (patientRef?.type === "reference" && eventRef?.type === "reference") {
          const {
            firstName,
            lastName
          } = parsePatientDisplayName(patientRef.reference.display);
          actions.push({
            label: "Open in Calendar",
            onClick: () => handleNavigate(getRouteForReference("event", eventRef.reference.id) ?? "/check-in/calendar", notification.id)
          }, {
            label: "Open in Sidebar",
            onClick: () => {
              openPatientSidebar(patientSidebarDefinition, {
                patientTiny: {
                  id: patientRef.reference.id,
                  firstName,
                  lastName,
                  type: UserType.PATIENT
                },
                selectedEventId: eventRef.reference.id
              });
              closeDrawer();
              if (currentUser?.notificationAutoMarkRead === NotificationAutoMarkRead.ON_QUICK_ACTION) {
                handleMarkAsRead([notification.id]);
              }
            }
          });
          return actions;
        }
      }
      for (const segment_1 of refs) {
        if (segment_1.type !== "reference") {
          continue;
        }
        const {
          type,
          id
        } = segment_1.reference;
        const label = QUICK_ACTION_LABEL[type];
        if (!label || seen.has(label)) {
          continue;
        }
        if (type === "teamconversation") {
          if (!isWhisperEnabled) {
            continue;
          }
          seen.add(label);
          actions.push({
            label,
            onClick: () => {
              openTeamChat();
              setActiveConversation(id);
              closeDrawer();
              handleMarkAsRead([notification.id]);
            }
          });
          continue;
        }
        const route = getRouteForReference(type, id);
        if (!route) {
          continue;
        }
        seen.add(label);
        actions.push({
          label,
          onClick: () => handleNavigate(route, notification.id)
        });
      }
      return actions;
    };
    $[41] = closeDrawer;
    $[42] = currentUser?.notificationAutoMarkRead;
    $[43] = handleMarkAsRead;
    $[44] = handleNavigate;
    $[45] = isWhisperEnabled;
    $[46] = openPatientSidebar;
    $[47] = openTeamChat;
    $[48] = setActiveConversation;
    $[49] = toast;
    $[50] = t18;
  } else {
    t18 = $[50];
  }
  currentUser?.notificationAutoMarkRead;
  const getQuickActions = t18;
  const clock = useClinicClock();
  let t19;
  if ($[51] !== clock || $[52] !== notifications) {
    t19 = groupNotifications(notifications, clock);
    $[51] = clock;
    $[52] = notifications;
    $[53] = t19;
  } else {
    t19 = $[53];
  }
  const {
    today,
    thisWeek,
    older
  } = t19;
  let t20;
  if ($[54] !== className) {
    t20 = cx("flex h-full flex-col", className);
    $[54] = className;
    $[55] = t20;
  } else {
    t20 = $[55];
  }
  let t21;
  if ($[56] === Symbol.for("react.memo_cache_sentinel")) {
    t21 = <h2 className="text-body-emphasis text-foreground">Notifications</h2>;
    $[56] = t21;
  } else {
    t21 = $[56];
  }
  let t22;
  if ($[57] !== closeDrawer || $[58] !== isLocked || $[59] !== lockDrawer || $[60] !== mode) {
    t22 = mode === "desktop" && <Button variant="ghost" className="text-muted-foreground hover:text-foreground !shadow-none size-9" onClick={isLocked ? closeDrawer : lockDrawer} tooltip={isLocked ? "Close" : "Keep open"} icon={isLocked ? HiChevronDoubleLeft : HiChevronDoubleRight} />;
    $[57] = closeDrawer;
    $[58] = isLocked;
    $[59] = lockDrawer;
    $[60] = mode;
    $[61] = t22;
  } else {
    t22 = $[61];
  }
  let t23;
  if ($[62] === Symbol.for("react.memo_cache_sentinel")) {
    t23 = <DropdownMenuTrigger asChild={true}><Button variant="ghost" className="text-muted-foreground hover:text-foreground !shadow-none size-9" tooltip="Filter" icon={HiOutlineFunnel} /></DropdownMenuTrigger>;
    $[62] = t23;
  } else {
    t23 = $[62];
  }
  let t24;
  if ($[63] !== filterRead || $[64] !== isAutoMarkOnOpen) {
    t24 = <DropdownMenu>{t23}<DropdownMenuContent align="end">{(["all", "unread", "read"] as FilterType[]).map(filter => <DropdownMenuItem key={filter} disabled={isAutoMarkOnOpen && filter === "unread"} onClick={() => setFilterRead(filter)} className={cx(filterRead === filter && "bg-muted")}>{filter.charAt(0).toUpperCase() + filter.slice(1)}</DropdownMenuItem>)}</DropdownMenuContent></DropdownMenu>;
    $[63] = filterRead;
    $[64] = isAutoMarkOnOpen;
    $[65] = t24;
  } else {
    t24 = $[65];
  }
  let t25;
  if ($[66] === Symbol.for("react.memo_cache_sentinel")) {
    t25 = <DropdownMenuTrigger asChild={true}><DropdownSettingsButton variant="ghost" className="text-muted-foreground hover:text-foreground size-9" /></DropdownMenuTrigger>;
    $[66] = t25;
  } else {
    t25 = $[66];
  }
  let t26;
  if ($[67] !== bulkActionMutation || $[68] !== toast) {
    t26 = () => {
      bulkActionMutation("mark_all_read", {
        onError: () => {
          toast({
            title: "Error",
            description: "Failed to mark notifications as read",
            variant: "error"
          });
        }
      });
    };
    $[67] = bulkActionMutation;
    $[68] = toast;
    $[69] = t26;
  } else {
    t26 = $[69];
  }
  let t27;
  if ($[70] === Symbol.for("react.memo_cache_sentinel")) {
    t27 = <HiOutlineCheck className="mr-2 h-4 w-4" />;
    $[70] = t27;
  } else {
    t27 = $[70];
  }
  let t28;
  if ($[71] !== t26) {
    t28 = <DropdownMenuItem onClick={t26}>{t27}Mark all as read</DropdownMenuItem>;
    $[71] = t26;
    $[72] = t28;
  } else {
    t28 = $[72];
  }
  let t29;
  if ($[73] !== bulkActionMutation || $[74] !== toast) {
    t29 = () => {
      bulkActionMutation("archive_all", {
        onError: () => {
          toast({
            title: "Error",
            description: "Failed to archive notifications",
            variant: "error"
          });
        }
      });
    };
    $[73] = bulkActionMutation;
    $[74] = toast;
    $[75] = t29;
  } else {
    t29 = $[75];
  }
  let t30;
  if ($[76] === Symbol.for("react.memo_cache_sentinel")) {
    t30 = <HiOutlineArchiveBox className="mr-2 h-4 w-4" />;
    $[76] = t30;
  } else {
    t30 = $[76];
  }
  let t31;
  if ($[77] !== t29) {
    t31 = <DropdownMenuItem onClick={t29}>{t30}Archive all</DropdownMenuItem>;
    $[77] = t29;
    $[78] = t31;
  } else {
    t31 = $[78];
  }
  let t32;
  if ($[79] !== bulkActionMutation || $[80] !== toast) {
    t32 = () => {
      bulkActionMutation("archive_read", {
        onError: () => {
          toast({
            title: "Error",
            description: "Failed to archive read notifications",
            variant: "error"
          });
        }
      });
    };
    $[79] = bulkActionMutation;
    $[80] = toast;
    $[81] = t32;
  } else {
    t32 = $[81];
  }
  let t33;
  if ($[82] === Symbol.for("react.memo_cache_sentinel")) {
    t33 = <HiOutlineArchiveBoxArrowDown className="mr-2 h-4 w-4" />;
    $[82] = t33;
  } else {
    t33 = $[82];
  }
  let t34;
  if ($[83] !== t32) {
    t34 = <DropdownMenuItem onClick={t32}>{t33}Archive read</DropdownMenuItem>;
    $[83] = t32;
    $[84] = t34;
  } else {
    t34 = $[84];
  }
  let t35;
  if ($[85] === Symbol.for("react.memo_cache_sentinel")) {
    t35 = <DropdownMenuSeparator />;
    $[85] = t35;
  } else {
    t35 = $[85];
  }
  let t36;
  if ($[86] !== handleNavigate) {
    t36 = () => handleNavigate("/settings/notifications");
    $[86] = handleNavigate;
    $[87] = t36;
  } else {
    t36 = $[87];
  }
  let t37;
  if ($[88] === Symbol.for("react.memo_cache_sentinel")) {
    t37 = <HiOutlineCog6Tooth className="mr-2 h-4 w-4" />;
    $[88] = t37;
  } else {
    t37 = $[88];
  }
  let t38;
  if ($[89] !== t36) {
    t38 = <DropdownMenuItem onClick={t36}>{t37}Notification settings</DropdownMenuItem>;
    $[89] = t36;
    $[90] = t38;
  } else {
    t38 = $[90];
  }
  let t39;
  if ($[91] !== t28 || $[92] !== t31 || $[93] !== t34 || $[94] !== t38) {
    t39 = <DropdownMenu>{t25}<DropdownMenuContent align="end">{t28}{t31}{t34}{t35}{t38}</DropdownMenuContent></DropdownMenu>;
    $[91] = t28;
    $[92] = t31;
    $[93] = t34;
    $[94] = t38;
    $[95] = t39;
  } else {
    t39 = $[95];
  }
  let t40;
  if ($[96] !== mode || $[97] !== onClose) {
    t40 = mode === "mobile" && onClose && <Button variant="ghost" className="h-7 w-7 p-0 text-muted-foreground hover:text-foreground" onClick={onClose} tooltip="Close" icon={HiXMark} />;
    $[96] = mode;
    $[97] = onClose;
    $[98] = t40;
  } else {
    t40 = $[98];
  }
  let t41;
  if ($[99] !== t22 || $[100] !== t24 || $[101] !== t39 || $[102] !== t40) {
    t41 = <div className="flex items-center justify-between border-b border-border px-4 shadow-md h-10 md:shadow-none">{t21}<div className="flex items-center gap-0.5">{t22}{t24}{t39}{t40}</div></div>;
    $[99] = t22;
    $[100] = t24;
    $[101] = t39;
    $[102] = t40;
    $[103] = t41;
  } else {
    t41 = $[103];
  }
  let t42;
  let t43;
  let t44;
  if ($[104] === Symbol.for("react.memo_cache_sentinel")) {
    t42 = <EprescribingBanner />;
    t43 = <BrowserNotificationsBanner />;
    t44 = <SoundBlockedBanner />;
    $[104] = t42;
    $[105] = t43;
    $[106] = t44;
  } else {
    t42 = $[104];
    t43 = $[105];
    t44 = $[106];
  }
  let t45;
  if ($[107] !== fetchNextPage || $[108] !== filterRead || $[109] !== getQuickActions || $[110] !== handleArchive || $[111] !== handleMarkAsRead || $[112] !== handleMarkAsUnread || $[113] !== handleMuteAlertType || $[114] !== hasNextPage || $[115] !== isFetchingNextPage || $[116] !== isLoading || $[117] !== notifications || $[118] !== older || $[119] !== thisWeek || $[120] !== today) {
    t45 = <div className="flex-1 overflow-y-auto">{isLoading ? <div>{[...Array(5)].map(_temp10)}</div> : notifications.length === 0 && !hasNextPage ? <div className="flex flex-col items-center justify-center py-16 text-center"><HiBell className="mb-3 h-10 w-10 text-muted-foreground/50" /><h3 className="mb-1 text-body-strong text-foreground">{filterRead === "unread" ? "You're all caught up!" : "No notifications yet"}</h3><p className="text-caption text-muted-foreground">{filterRead === "unread" ? "No unread notifications." : "When you receive notifications, they'll appear here."}</p></div> : <div>{[{
          label: "Today",
          items: today
        }, {
          label: "This week",
          items: thisWeek
        }, {
          label: "Older",
          items: older
        }].map(group => <NotificationGroup key={group.label} label={group.label} notifications={group.items} getQuickActions={getQuickActions} onMarkAsRead={handleMarkAsRead} onMarkAsUnread={handleMarkAsUnread} onArchive={handleArchive} onMuteAlertType={handleMuteAlertType} />)}{hasNextPage && <div ref={observerTarget} className="flex justify-center py-4">{isFetchingNextPage ? <p className="text-caption text-muted-foreground">Loading...</p> : <Button variant="ghost" size="sm" onClick={() => fetchNextPage()}>Load More</Button>}</div>}</div>}</div>;
    $[107] = fetchNextPage;
    $[108] = filterRead;
    $[109] = getQuickActions;
    $[110] = handleArchive;
    $[111] = handleMarkAsRead;
    $[112] = handleMarkAsUnread;
    $[113] = handleMuteAlertType;
    $[114] = hasNextPage;
    $[115] = isFetchingNextPage;
    $[116] = isLoading;
    $[117] = notifications;
    $[118] = older;
    $[119] = thisWeek;
    $[120] = today;
    $[121] = t45;
  } else {
    t45 = $[121];
  }
  let t46;
  if ($[122] !== t20 || $[123] !== t41 || $[124] !== t45) {
    t46 = <div className={t20}>{t41}{t42}{t43}{t44}{t45}</div>;
    $[122] = t20;
    $[123] = t41;
    $[124] = t45;
    $[125] = t46;
  } else {
    t46 = $[125];
  }
  return t46;
}
function _temp10(_, index) {
  return <div key={index} className="flex gap-3 border-b border-border px-4 py-3"><div className="h-8 w-8 animate-pulse rounded-full bg-muted" /><div className="flex-1 space-y-2"><div className="h-4 w-3/4 animate-pulse rounded bg-muted" /><div className="h-3 w-1/2 animate-pulse rounded bg-muted" /></div></div>;
}
function _temp9(segment_0) {
  return segment_0.type === "reference" && segment_0.reference.type === "event";
}
function _temp8(segment) {
  return segment.type === "reference" && segment.reference.type === "patient";
}
function _temp7(n_1) {
  return n_1.alert.content;
}
function _temp6(s_0) {
  return s_0.setActiveConversation;
}
function _temp5(s) {
  return s.openDrawer;
}
function _temp4(n_0) {
  return n_0.id;
}
function _temp3(n) {
  return !n.read;
}
function _temp2(state) {
  return state.open;
}
function _temp(page) {
  return page?.data ?? [];
}